/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  CompanyContainer,
  HeaderTitle,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import { DropdownButton, Loader, Table, TabButton } from '../../../Components/uielements';
import TimesheetService from '../../../services/bioplus-services/timesheet';
import { IntlMessages } from '../../../helper/langHelper';
import moment from 'moment';
import { convertToUserTimeZone } from '../../../helper/dataFormat';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import CheckIcon from '@mui/icons-material/Check';
import GroupIcon from '@mui/icons-material/Group';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import { useUserContext } from '../../../hooks/UserContext';

function extractData(dataArray, userTimeZone) {
  if (dataArray) {
    const extractedData = dataArray?.map((item) => {
      return {
        name: `${item?.userId?.firstName} ${item?.userId?.lastName}`,
        geofenceName: item?.geofenceId?.name,
        date: moment(item?.recordDate).format('DD-MM-YYYY'),
        checkIn: item?.checkin ? convertToUserTimeZone(item?.checkin, userTimeZone) : '--',
        checkOut: item?.checkout ? convertToUserTimeZone(item?.checkout, userTimeZone) : '--',
        createdAt: moment(item?.recordDate).format('DD-MM-YYYY'),
      };
    });
    return { bioplusUsers: extractedData };
  }
}

const getTimesheetDataBasedOnActiveTab = async (selectedTab, page, pageSize, companyId) => {
  if (selectedTab === 'Visited') {
    return await TimesheetService.listTimesheet(page, pageSize);
  } else if (selectedTab === 'Guests') {
    return await TimesheetService.listTimesheetGuest(page, pageSize, companyId);
  }
};

const getReportDataBasedOnActiveTab = async (selectedTab, companyId) => {
  if (selectedTab === 'Visited') {
    return await TimesheetService.listTimesheetForReport();
  } else if (selectedTab === 'Guests') {
    return await TimesheetService.listTimesheetGuestForReport(companyId);
  }
};

const Attendance = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedTab, setSelectedTab] = useState('Visited');

  const userContext = useUserContext();
  const userRole = userContext.company;
  const companyId = userRole?.companyId?.id;

  const query = useQuery(['timesheetEntries', page, pageSize, selectedTab], () =>
    getTimesheetDataBasedOnActiveTab(selectedTab, page, pageSize, companyId)
  );

  const timesheetsForReport = useQuery(['timesheetEntriesReport', selectedTab], () =>
    getReportDataBasedOnActiveTab(selectedTab, companyId)
  );

  const userTableData = query?.data?.data || [];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };

  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleSelectedTab = (selectedTab) => {
    setSelectedTab(selectedTab);
    setPage(0);
  };

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find(
    (permission) => permission?.label === 'timesheet'
  );

  const canDownloadReport = !!isUserAllowToManage?.isReport;

  const userColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName ?? '--'} ${
              cellData?.row?.original?.userId?.lastName ?? '--'
            }`}
          </TableCell>
        ),
        width: '20%',
      },
      {
        Header: <IntlMessages id="table.col.geofence.name" />,
        accessor: 'geofence',
        Cell: (cellData) => (
          <TableCell>{`${cellData?.row?.original?.geofenceId?.name ?? '--'}`}</TableCell>
        ),
        width: '20%',
      },
      {
        Header: <IntlMessages id="table.col.date" />,
        accessor: 'recordDate',
        width: '16%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.checkIn" />,
        accessor: 'checkin',
        width: '15%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? convertToUserTimeZone(cellData.value, userTimeZone) : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.checkOut" />,
        accessor: 'checkout',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? convertToUserTimeZone(cellData.value, userTimeZone) : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
    ],
    []
  );

  const excelFormatData = extractData(timesheetsForReport?.data, userTimeZone);
  const fileName = `Timesheets-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const fileNameCsv = `Timesheets-${moment().format('DD-MM-YYYY-hh.mm A')}.csv`;
  const columnConfig = [
    { header: 'Name', key: 'name', width: 30 },
    { header: 'Geofence Name', key: 'geofenceName', width: 30 },
    { header: 'Date', key: 'date', width: 30 },
    { header: 'Check In', key: 'checkIn', width: 30 },
    { header: 'Check Out', key: 'checkOut', width: 30 },
    { header: 'created At', key: 'createdAt', width: 30 },
  ];

  const handleClickItem = (item) => {
    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  if (query.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.records.attendance" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '10px' }}>
          <TabButton
            buttonArray={[
              { buttonText: 'Visited', textId: '1', icon: <CheckIcon /> },
              { buttonText: 'Guests', textId: '2', icon: <GroupIcon /> },
            ]}
            onButtonClick={handleSelectedTab}
            selectedTab={selectedTab}
          />
          {userTableData && userTableData.length > 0 && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
        </TwoColumns>
      </CompanyContainer>
      <TableWrapper>
        <Table
          data={userTableData}
          columns={userColumns}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={query?.data?.total || 0}
        />
      </TableWrapper>
    </StyledContainer>
  );
};

export default Attendance;
